import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import BackButton from "@/components/BackButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Gradebook() {
  const [data, setData] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get("/teacher/gradebook");
      setData(res.data);
      if (res.data.length) setActiveId(res.data[0].course_id);
      setLoading(false);
    })();
  }, []);

  const active = useMemo(
    () => data.find((c) => c.course_id === activeId),
    [data, activeId]
  );

  return (
    <div className="p-6 md:p-10">
      <BackButton />
      <div className="mt-5 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /gradebook
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">
            Gradebook
          </h1>
        </div>
        <Select value={activeId} onValueChange={setActiveId}>
          <SelectTrigger className="w-72 rounded-none border-foreground" data-testid="gradebook-course-select">
            <SelectValue placeholder="Select course" />
          </SelectTrigger>
          <SelectContent>
            {data.map((c) => (
              <SelectItem key={c.course_id} value={c.course_id}>
                {c.course_title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="h-64 animate-pulse border border-border bg-muted" />
      ) : !active ? (
        <div className="p-10 text-center text-sm text-muted-foreground">
          No courses to display.
        </div>
      ) : (
        <div className="border-2 border-foreground bg-background">
          <div className="mono grid grid-cols-12 gap-2 border-b border-border bg-muted px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-4">Student</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Progress</div>
            <div className="col-span-2 text-right">Quiz avg</div>
          </div>
          {active.students.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No enrolments in this course yet.
            </div>
          ) : (
            active.students.map((s, i) => {
              const scores = Object.values(s.quiz_scores || {});
              const avg =
                scores.length === 0
                  ? null
                  : Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
              return (
                <div
                  key={i}
                  className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-b-0"
                >
                  <div className="col-span-4 font-semibold">{s.student_name}</div>
                  <div className="col-span-3 mono text-[11px] text-muted-foreground">
                    {s.student_email}
                  </div>
                  <div className="col-span-3">
                    <Progress value={s.progress_pct || 0} className="h-2 rounded-none" />
                    <div className="mono mt-1 text-[10px] text-muted-foreground">
                      {(s.progress_pct || 0).toFixed(0)}%
                    </div>
                  </div>
                  <div className="col-span-2 text-right font-mono font-semibold">
                    {avg != null ? `${avg}%` : "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
